/* Color recommendations share the live storefront catalog with the PDP and collection filters. */
(() => {
  if (customElements.get('wolf-cart-colors')) return;

  const colorOptionNames = /^(colou?r|glaze|colou?rway|pattern)$/i;

  function dominantColor(items, products = [], minimumUnits = 2) {
    const totals = new Map();
    const catalog = new Map(products.map((product) => [String(product.id), product]));

    items.forEach((item) => {
      const quantity = Number(item.quantity);
      if (!Number.isFinite(quantity) || quantity <= 0) return;

      const product = catalog.get(String(item.product_id));
      const variant = product?.variants.find((candidate) => String(candidate.id) === String(item.variant_id || item.id));
      const option = item.options_with_values?.find((value) => colorOptionNames.test(String(value.name).trim()));
      const color = String(variant?.color || option?.value || '').trim();
      const key = window.WolfColors.normalizeColor(color);
      if (!key) return;

      const existing = totals.get(key);
      if (existing) {
        existing.quantity += quantity;
      } else {
        totals.set(key, { key, color, quantity, swatch: variant?.swatch, brandColor: variant?.brandColor });
      }
    });

    // A repeated color is meaningful even when the units belong to one line item.
    // Alphabetical ties do not shift when Shopify changes the line item ordering.
    return [...totals.values()]
      .filter((entry) => entry.quantity >= minimumUnits)
      .sort((left, right) => right.quantity - left.quantity || left.key.localeCompare(right.key))[0] || null;
  }

  class WolfCartColors extends HTMLElement {
    connectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.track = this.querySelector('[data-cart-color-products]');
      this.previousButton = this.querySelector('[data-cart-colors-previous]');
      this.nextButton = this.querySelector('[data-cart-colors-next]');
      this.pagedCarousel = this.dataset.carousel === 'paged';
      if (!this.track || !window.WolfColors) return;

      this.abortListeners = new AbortController();
      const options = { signal: this.abortListeners.signal };
      if (!this.pagedCarousel) {
        this.previousButton.addEventListener('click', () => this.move(-1), options);
        this.nextButton.addEventListener('click', () => this.move(1), options);
        this.track.addEventListener('scroll', () => this.updateControls(), { ...options, passive: true });
        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        this.resizeObserver.observe(this.track);
      }
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        (event.key === 'ArrowRight' ? this.nextButton : this.previousButton).click();
      }, options);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData } = {}) => {
          if (!this.isConnected) return;
          // add.js returns only the items added; item_count identifies a complete cart.
          if (Array.isArray(cartData?.items) && typeof cartData.item_count === 'number') {
            this.renderCart(cartData);
          } else {
            this.fetchCart();
          }
        });
      }

      try {
        const initialCart = JSON.parse(this.querySelector('[data-cart-colors-state]').textContent);
        this.renderCart(initialCart);
      } catch (_) {
        this.fetchCart();
      }
    }

    disconnectedCallback() {
      this.generation += 1;
      this.abortCart?.abort();
      this.abortListeners?.abort();
      this.resizeObserver?.disconnect();
      this.unsubscribeCart?.();
      this.cleanupCarousel?.();
    }

    async fetchCart() {
      if (!this.isConnected) return;
      this.abortCart?.abort();
      const controller = new AbortController();
      this.abortCart = controller;
      const generation = ++this.generation;
      this.hidden = true;

      try {
        const url = this.dataset.cartUrl || `${window.Shopify?.routes?.root || '/'}cart.js`;
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Could not load cart recommendations.');
        const cart = await response.json();
        if (!this.isConnected || generation !== this.generation) return;
        this.renderCart(cart);
      } catch (_) {
        // Recommendations are optional; cart editing and checkout stay available.
        if (generation === this.generation) this.hidden = true;
      }
    }

    async renderCart(cart) {
      if (!this.isConnected) return;
      this.abortCart?.abort();
      const generation = ++this.generation;
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const minimumUnits = Math.max(1, Number(this.dataset.minimumUnits) || 2);
      this.cleanupCarousel?.();
      this.cleanupCarousel = null;
      this.hidden = true;
      if (items.reduce((total, item) => total + Number(item.quantity || 0), 0) < minimumUnits) return;

      try {
        const products = await window.WolfColors.loadCatalog();
        if (!this.isConnected || generation !== this.generation) return;
        const dominant = dominantColor(items, products, minimumUnits);
        if (!dominant) return;

        const matches = window.WolfColors.matchingProducts(products, dominant.color, {
          excludeProductIds: items.map((item) => item.product_id),
          limit: Number(this.dataset.limit) || 8,
        });
        if (!matches.length) return;

        const colors = window.WolfColors.colorStyle(dominant.color, dominant.swatch, dominant.brandColor);
        this.style.setProperty('--wolf-color-background', colors.background);
        this.style.setProperty('--wolf-color-foreground', colors.foreground);
        if (this.pagedCarousel) {
          this.style.setProperty('--wolf-slider-arrow-color', ['White', 'Yellow'].includes(colors.brandColor) ? colors.foreground : colors.background);
        }
        this.querySelector('[data-cart-color-name]').textContent = dominant.color.toLocaleLowerCase();
        this.querySelector('[data-cart-color-link]').href = window.WolfColors.shopColorUrl(dominant.color);
        this.querySelector('[data-cart-colors-status]').textContent = `${matches.length} more pieces in ${dominant.color}.`;
        this.track.replaceChildren(...matches.map((match) => window.WolfColors.renderCard(match)));
        this.track.scrollLeft = 0;
        this.hidden = false;
        if (this.pagedCarousel) this.cleanupCarousel = window.WolfColors.setupCarousel(this);
        else this.updateControls();
      } catch (_) {
        if (generation === this.generation) this.hidden = true;
      }
    }

    move(direction) {
      const card = this.track.firstElementChild;
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({
        left: direction * (card.getBoundingClientRect().width + gap),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }

    updateControls() {
      if (!this.track) return;
      this.previousButton.disabled = this.track.scrollLeft <= 2;
      this.nextButton.disabled = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    }
  }

  customElements.define('wolf-cart-colors', WolfCartColors);
})();
